/**
 * Shopper notifications (docs/notifications.md): the bell's queries and mutations, the handler
 * that toasts what the shopper's realtime channel delivers, and the poll that stands in for it
 * while the channel is not live. Hooks and helpers only; the components are under
 * `components/notifications/`.
 */
import {
  isNotificationActionUrl,
  type MarkNotificationsReadRequest,
  type Notification,
  type NotificationCategory,
  type NotificationListResponse,
  type NotificationSettingsResponse,
  notificationChannel,
  notificationEventSchema,
} from "@loot-os/api-contract";
import type { LootClient } from "@loot-os/client";
import { NotificationCategoryIcon } from "@loot-os/design-system/domain/notifications/NotificationCategoryIcon";
import {
  type InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { createElement, useEffect, useState } from "react";
import { lootMeQueryOptions } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useShopper } from "./shopper.js";
import { type TenantConfig, useTenant } from "./tenant.js";
import { showToast } from "./toasts.js";
import { getRealtimeClient } from "./useBattleChannel.js";

/** The panel's two tabs: every notification, or the unread ones (`?unread=true`). */
export type NotificationFilter = "all" | "unread";

/** How often the unread count is asked for while no live channel tells us (and the tab shows). */
export const NOTIFICATION_POLL_MS = 60_000;

const PAGE_SIZE = 20;
/** The most a poll toasts at once; older arrivals wait in the panel. */
const POLL_TOAST_LIMIT = 5;

// Everything is under "private", so a sign-out sweeps it with the rest of the shopper's data.
export const notificationKeys = {
  lists: ["private", "notifications", "list"] as const,
  list: (filter: NotificationFilter) => ["private", "notifications", "list", filter] as const,
  unreadCount: ["private", "notifications", "unread-count"] as const,
  settings: ["private", "notifications", "settings"] as const,
};

/** The notifications module is on; a config cached before the field existed reads off. */
export function notificationsEnabled(config: TenantConfig): boolean {
  return (config as Partial<TenantConfig>).notifications?.enabled === true;
}

// ---- seen ------------------------------------------------------------------------------------

// Every notification this page has shown, in a toast or in the panel, so neither the channel nor
// the poll toasts one twice.
const seenIds = new Set<string>();

function markSeen(notifications: readonly { id: string }[]): void {
  for (const notification of notifications) seenIds.add(notification.id);
}

/** Tests only. */
export function resetSeenNotifications(): void {
  seenIds.clear();
}

// ---- toasts ----------------------------------------------------------------------------------

/** One toast: the notification's title, its body and its icon in the category's tint. */
export function toastNotification(
  notification: Pick<Notification, "id" | "title" | "body" | "category" | "icon">,
): void {
  showToast({
    // One toast per notification: a repeat updates the one already up.
    id: `notification:${notification.id}`,
    title: notification.title,
    description: notification.body || undefined,
    media: createElement(NotificationCategoryIcon, {
      category: notification.category,
      icon: notification.icon,
      size: "sm",
    }),
  });
}

/** What the realtime channel carried: toast it, count it unread, and refresh the lists. */
export function handleNotificationEvent(raw: unknown): void {
  const parsed = notificationEventSchema.safeParse(raw);
  if (!parsed.success) return;
  const { notification } = parsed.data;
  if (seenIds.has(notification.id)) return;
  seenIds.add(notification.id);
  toastNotification(notification);
  if (notification.readAt === null) {
    const count = queryClient.getQueryData<number>(notificationKeys.unreadCount);
    if (count === undefined) {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount });
    } else {
      queryClient.setQueryData(notificationKeys.unreadCount, count + 1);
    }
  }
  void queryClient.invalidateQueries({ queryKey: notificationKeys.lists });
}

/**
 * The unread count. When it rose since the last answer, the newest unread notifications this page
 * has not shown are toasted, no more than the rise: what a live channel would have toasted.
 */
export async function fetchUnreadCount(client: LootClient): Promise<number> {
  const previous = queryClient.getQueryData<number>(notificationKeys.unreadCount);
  const unreadCount = await client.getUnreadNotificationCount();
  if (previous !== undefined && unreadCount > previous) {
    void toastArrivals(client, unreadCount - previous);
  }
  return unreadCount;
}

async function toastArrivals(client: LootClient, rise: number): Promise<void> {
  const page = await client.listNotifications({ unread: true, limit: POLL_TOAST_LIMIT });
  const unseen = page.data.filter((notification) => !seenIds.has(notification.id));
  markSeen(page.data);
  // Newest first on the wire; toasted oldest first, so the newest ends up on top.
  for (const notification of unseen.slice(0, rise).reverse()) toastNotification(notification);
  void queryClient.invalidateQueries({ queryKey: notificationKeys.lists });
}

// ---- queries ---------------------------------------------------------------------------------

/** One tab's notifications, newest first; every page also refreshes the unread count. */
export const notificationsInfiniteQueryOptions = (client: LootClient, filter: NotificationFilter) =>
  infiniteQueryOptions({
    queryKey: notificationKeys.list(filter),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<NotificationListResponse> => {
      const page = await client.listNotifications({
        limit: PAGE_SIZE,
        ...(filter === "unread" ? { unread: true } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      });
      markSeen(page.data);
      queryClient.setQueryData(notificationKeys.unreadCount, page.unreadCount);
      return page;
    },
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    // Fetched whenever the panel opens.
    staleTime: 0,
  });

export const unreadNotificationCountQueryOptions = (client: LootClient, opts: { poll: boolean }) =>
  queryOptions({
    queryKey: notificationKeys.unreadCount,
    queryFn: () => fetchUnreadCount(client),
    retry: false,
    staleTime: 30_000,
    // React Query skips the interval while the tab is hidden.
    refetchInterval: opts.poll ? NOTIFICATION_POLL_MS : false,
  });

export const notificationSettingsQueryOptions = (client: LootClient) =>
  queryOptions({
    queryKey: notificationKeys.settings,
    queryFn: () => client.getNotificationSettings(),
    retry: false,
    staleTime: 60_000,
  });

// ---- hooks -----------------------------------------------------------------------------------

/** The bell's once-a-session ring for unread notifications, per shopper, in this tab's session. */
const bellRangKey = (userId: string) => `loot:bell-rang:${userId}`;

/**
 * Whether the bell has already rung for this shopper's unread notifications in this browser
 * session. Storage that can't be read counts as not yet, so the worst case is a ring a visit.
 */
export function bellRangThisSession(userId: string): boolean {
  try {
    return sessionStorage.getItem(bellRangKey(userId)) === "1";
  } catch {
    return false;
  }
}

/** Records that the bell rang for this shopper's unread notifications in this browser session. */
export function markBellRang(userId: string): void {
  try {
    sessionStorage.setItem(bellRangKey(userId), "1");
  } catch {
    // Storage blocked: the bell may ring again on the next load.
  }
}

/**
 * The signed-in shopper's unread count, kept current by their own realtime channel when the
 * stage has one and by a poll while that channel is not live; both toast what arrives.
 * Undefined until the first count arrives.
 */
export function useUnreadNotifications(): number | undefined {
  const { config } = useTenant();
  const { client, sessionToken } = useShopper();
  const realtime = getRealtimeClient(config.realtime);
  const environmentId = config.environmentId;
  const signedIn = !!sessionToken && notificationsEnabled(config);
  // The channel is named by the Loot OS end user, which only GET /v1/me says.
  const me = useQuery({ ...lootMeQueryOptions(), enabled: signedIn && !!realtime });
  const endUserId = me.data?.id ?? null;
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!realtime || !endUserId || !signedIn) return;
    const unsubscribe = realtime.subscribe(
      notificationChannel(environmentId, endUserId),
      handleNotificationEvent,
      (status) => {
        setLive(status === "live");
        // AppSync replays nothing a dropped socket missed; a recount toasts what arrived.
        if (status === "live") {
          void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount });
        }
      },
    );
    return () => {
      unsubscribe();
      setLive(false);
    };
  }, [realtime, environmentId, endUserId, signedIn]);

  const count = useQuery({
    ...unreadNotificationCountQueryOptions(client, { poll: !live }),
    enabled: signedIn,
  });
  return count.data;
}

/** The time, re-read every minute, for the rows' "12m" and the Today / Earlier split. */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** Applies a read to every cached list, and to the count, before the server answers. */
function applyReadLocally(request: MarkNotificationsReadRequest): void {
  const readAt = new Date().toISOString();
  const named = "ids" in request ? new Set(request.ids) : null;
  let newlyRead = 0;
  const seen = new Set<string>();
  queryClient.setQueriesData<InfiniteData<NotificationListResponse>>(
    { queryKey: notificationKeys.lists },
    (prev) =>
      prev && {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          data: page.data.map((notification) => {
            if (notification.readAt !== null || (named && !named.has(notification.id))) {
              return notification;
            }
            if (!seen.has(notification.id)) {
              seen.add(notification.id);
              newlyRead += 1;
            }
            return { ...notification, readAt };
          }),
        })),
      },
  );
  queryClient.setQueryData<number>(notificationKeys.unreadCount, (prev) =>
    prev === undefined ? prev : named ? Math.max(0, prev - newlyRead) : 0,
  );
}

/** Marks the named notifications, or all of them, read; the count comes back with the answer. */
export function useMarkNotificationsRead() {
  const { client } = useShopper();
  return useMutation({
    mutationFn: (request: MarkNotificationsReadRequest) => client.markNotificationsRead(request),
    onMutate: applyReadLocally,
    onSuccess: (res) => queryClient.setQueryData(notificationKeys.unreadCount, res.unreadCount),
    // Put back what the server holds.
    onError: () => void queryClient.invalidateQueries({ queryKey: ["private", "notifications"] }),
  });
}

/** The settings with one category switched, and the opt-out list the PUT replaces whole. */
export function toggleNotificationCategory(
  settings: NotificationSettingsResponse,
  category: NotificationCategory,
  enabled: boolean,
): { next: NotificationSettingsResponse; optedOut: NotificationCategory[] } {
  const categories = settings.categories.map((setting) =>
    setting.category === category && !setting.required ? { ...setting, enabled } : setting,
  );
  return {
    next: { categories },
    optedOut: categories
      .filter((setting) => !setting.required && !setting.enabled)
      .map((setting) => setting.category),
  };
}

const SETTINGS_MUTATION_KEY = ["notification-settings"] as const;

/**
 * Switches one category on or off at once and saves the whole opt-out list; a refusal puts the
 * switch back and says so.
 */
export function useToggleNotificationCategory() {
  const { client } = useShopper();
  const mutation = useMutation({
    mutationKey: SETTINGS_MUTATION_KEY,
    mutationFn: (vars: { next: NotificationSettingsResponse; optedOut: NotificationCategory[] }) =>
      client.updateNotificationSettings({ optedOut: vars.optedOut }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.settings });
      const previous = queryClient.getQueryData<NotificationSettingsResponse>(
        notificationKeys.settings,
      );
      queryClient.setQueryData(notificationKeys.settings, vars.next);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(notificationKeys.settings, context.previous);
      showToast({ kind: "error", title: "Your notification settings did not save. Try again." });
    },
    onSuccess: (res) => {
      // A later switch is still saving; its answer is the one to keep.
      if (queryClient.isMutating({ mutationKey: SETTINGS_MUTATION_KEY }) <= 1) {
        queryClient.setQueryData(notificationKeys.settings, res);
      }
    },
  });
  return {
    toggle: (category: NotificationCategory, enabled: boolean) => {
      const current = queryClient.getQueryData<NotificationSettingsResponse>(
        notificationKeys.settings,
      );
      if (current) mutation.mutate(toggleNotificationCategory(current, category, enabled));
    },
  };
}

// ---- actions ---------------------------------------------------------------------------------

/**
 * Where a row leads: a storefront path for the router (split into the parts it takes), an https
 * page for a new tab, or nowhere.
 */
export type NotificationTarget =
  | { kind: "path"; pathname: string; search: Record<string, string>; hash: string }
  | { kind: "external"; href: string };

export function notificationTarget(
  notification: Pick<Notification, "action">,
): NotificationTarget | null {
  const url = notification.action?.url;
  if (!url || !isNotificationActionUrl(url)) return null;
  if (!url.startsWith("/")) return { kind: "external", href: url };
  const parsed = new URL(url, "https://storefront.invalid");
  return {
    kind: "path",
    pathname: parsed.pathname,
    search: Object.fromEntries(parsed.searchParams),
    hash: parsed.hash.slice(1),
  };
}
